import { ESymbolCategory, ISymbolTickRulesGroup } from '../../market';

export enum EOrderType {
  BUY,
  SELL,
  BUY_LIMIT,
  SELL_LIMIT,
  BUY_STOP,
  SELL_STOP,
  BALANCE,
  CREDIT,
  BUY_STOP_LIMIT,
  SELL_STOP_LIMIT,
  CLOSE_BY,
  BUY_MARKET_TO_LIMIT,
  SELL_MARKET_TO_LIMIT
}

export enum EOrderWebsocketMessageType {
  ADD_ORDER,
  CLOSE_ORDER,
  UPDATE_ORDER
}

export enum EOrderHistoryType {
  ORDER = 'ORDER',
  DEAL = 'DEAL',
  POSITION = 'POSITION'
}

export enum EFillPolicy {
  FOK,
  IOC,
  RETURN,
  GTC,
  GTD,
  DAY
}

export enum EFillPolicyValue {
  FOK = 'FOK',
  IOC = 'IOC',
  RETURN = 'RETURN',
  GTC = 'GTC',
  GTD = 'GTD',
  DAY = 'DAY'
}

export enum EOrderExpiry {
  GTC = 'GTC',
  GTD = 'GTD',
  SPECIFIED_TIMESTAMP = 'SPECIFIED_TIMESTAMP',
  SPECIFIED_DAY = 'SPECIFIED_DAY'
}

export enum EOrderIndicate {
  POINTS = 'POINTS',
  PRICE = 'PRICE'
}

export enum EMarginMode {
  MARGIN_CALC_FOREX,
  MARGIN_CALC_CFD,
  MARGIN_CALC_FUTURES,
  MARGIN_CALC_CFDINDEX,
  MARGIN_CALC_CFDLEVERAGE,
  TRADE_MODE_FOREX_NO_LEVERAGE,
  TRADE_MODE_EXCH_STOCKS = 32,
  TRADE_MODE_EXCH_FUTURES = 33,
  TRADE_MODE_EXCH_FORTS = 34,
  TRADE_MODE_EXCH_OPTIONS = 35,
  TRADE_MODE_EXCH_OPTIONS_MARGIN = 36,
  TRADE_MODE_EXCH_BONDS = 37,
  TRADE_MODE_EXCH_STOCKS_MOEX = 38,
  TRADE_MODE_EXCH_BONDS_MOEX = 39,
  TRADE_MODE_SERV_COLLATERAL = 64
}

export enum EProfitMode {
  PROFIT_CALC_FOREX = 0,
  PROFIT_CALC_CFD = 1,
  PROFIT_CALC_FUTURES = 2,
  PROFIT_CALC_CFDINDEX = 3,
  PROFIT_CALC_CFDLEVERAGE = 4,
  PROFIT_CALC_FOREX_NO_LEVERAGE = 5,
  PROFIT_CALC_EXCH_STOCKS = 32,
  PROFIT_CALC_EXCH_FUTURES = 33,
  PROFIT_CALC_EXCH_FORTS = 34,
  PROFIT_CALC_EXCH_OPTIONS = 35,
  PROFIT_CALC_EXCH_OPTIONS_MARGIN = 36,
  PROFIT_CALC_EXCH_BONDS = 37,
  PROFIT_CALC_EXCH_STOCKS_MOEX = 38,
  PROFIT_CALC_EXCH_BONDS_MOEX = 39,
  PROFIT_CALC_SERV_COLLATERAL = 64
}

export enum EExecutionMode {
  EXE_REQUEST,
  EXE_INSTANT,
  EXE_MARKET,
  EXE_EXCHANGE
}

export enum EOrderReason {
  CLIENT,
  EXPERT,
  DEALER,
  SIGNAL,
  GATEWAY,
  MOBILE,
  WEB,
  API,
  SL,
  TP,
  SO,
  ROLLOVER,
  EXTERNAL_CLIENT,
  VMARGIN,
  SETTLEMENT,
  TRANSFER,
  SYNC,
  EXTERNAL_SERVICE,
  MIGRATION,
  SPLIT
}

export enum EDealReason {
  CLIENT,
  EXPERT,
  DEALER,
  SL,
  TP,
  SO,
  ROLLOVER,
  EXTERNAL_CLIENT,
  VMARGIN,
  GATEWAY,
  SIGNAL,
  SETTLEMENT,
  TRANSFER,
  SYNC,
  EXTERNAL_SERVICE,
  MIGRATION,
  MOBILE,
  WEB,
  SPLIT
}

export enum EOrderState {
  OPEN_NORMAL,
  OPEN_REMAND,
  OPEN_RESTORED,
  CLOSED_NORMAL,
  CLOSED_PART,
  CLOSED_BY,
  DELETED,
  STARTED,
  PLACED,
  CANCELED,
  PARTIAL,
  FILLED,
  REJECTED,
  EXPIRED,
  REQUEST_ADD,
  REQUEST_MODIFY,
  REQUEST_CANCEL
}

export enum EMetatraderOrderStatus {
  CLOSED = 'CLOSED',
  EXPIRED = 'EXPIRED',
  DELETED = 'DELETED',
  CREDITED = 'CREDITED'
}

export enum EOrderStateCentroid {
  CREATED,
  NEW,
  FILLED,
  PARTIALLY_FILLED,
  REJECTED,
  CANCELED
}

export enum EOrderDealType {
  BUY,
  SELL,
  BALANCE,
  CREDIT,
  CHARGE,
  CORRECTION,
  BONUS,
  COMMISSION,
  COMMISSION_DAILY,
  COMMISSION_MONTHLY,
  AGENT_DAILY,
  AGENT_MONTHLY,
  INTERESTRATE,
  BUY_CANCELED,
  SELL_CANCELED,
  DIVIDEND,
  DIVIDEND_FRANKED,
  TAX,
  AGENT,
  SO_COMPENSATION,
  SO_COMPENSATION_CREDIT
}

export enum EOrderDealDirection {
  IN,
  OUT,
  INOUT,
  OUT_BY
}

export enum EOrderStatus {
  CLOSED = 'CLOSED',
  EXPIRED = 'EXPIRED',
  DELETED = 'DELETED',
  CREDITED = 'CREDITED',
  NEW = 'NEW', // 0
  PARTIALLY_FILLED = 'PARTIALLY_FILLED', // 1
  FILLED = 'FILLED', // 2
  DONE_FOR_DAY = 'DONE_FOR_DAY', // 3
  CANCELED = 'CANCELED', // 4
  REPLACED = 'REPLACED', // 5
  PENDING_CANCEL = 'PENDING_CANCEL', // 6
  STOPPED = 'STOPPED', // 7
  REJECTED = 'REJECTED', // 8
  SUSPENDED = 'SUSPENDED', // 9
  PENDING_NEW = 'PENDING_NEW', // 10
  CALCULATED = 'CALCULATED', // 11
  ACCEPTED_FOR_BIDDING = 'ACCEPTED_FOR_BIDDING', // 13
  PENDING_REPLACE = 'PENDING_REPLACE' // 14,
}

export enum EOrderBalanceType {
  SUBSCRIPTION,
  TOP_UP,
  WITHDRAWAL,
  UPFRONT_FEE,
  PERFORMANCE_FEE,
  UNSUBSCRIPTION,
  MANAGEMENT_FEE,
  PENALTY_FEE,
  DEPOSIT,
  OTHERS
}

// Order APIs params
export interface IGetOrderBySymbolParams {
  symbol?: string;
}

export interface IGetOrderByIdParams {
  orderId: string;
}

export interface IGetOrderByIdV2Params {
  orderId: string;
}

export interface IGetDealHistoryParams {
  from: number;
  to: number;
}

export interface IGetOrderHistoryParams extends IGetOrderBySymbolParams {
  from: number;
  to: number;
  type: EOrderHistoryType;
}

// Order APIs responses
export interface IOrder {
  orderId: string;
  symbol: string;
  currency: string;
  volume: number;
  lotSize: number;
  lotSizeStep: number;
  minContractLotSize: number;
  swap: number;
  decimal: number;
  contractSize: number;
  units: number;
  unfilledVolume: number;
  openPrice: number;
  closePrice: number;
  currentPrice: number;
  priceTrigger: number;
  takeProfit: number;
  stopLoss: number;
  profit: number;
  tickSize: number;
  tickValue: number;
  commission: number;
  exchangeRate: number;
  marginMode: EMarginMode;
  marginRate: number;
  marginPercentage: number;
  pointStep: number;
  indicate: EOrderIndicate;
  exeMode: EExecutionMode;
  orderType: EOrderType;
  state: EOrderState;
  reason: EOrderReason;
  tradeExpiry: EOrderExpiry;
  fillPolicy: EFillPolicy;
  marketFillPolicy: EFillPolicy[];
  profitMode: EProfitMode;
  comment: string;
  exitMethod: string;
  openTime: number;
  closeTime: number;
  expiration: number;
  isEnable: boolean;
  description: string;
  boughtQuantity: number;
  soldQuantity: number;
  avgBoughtPrice: number;
  avgSoldPrice: number;
  lastPrice: number;
  marketValue: number;
  pnlGross: number;
  symbolType: ESymbolCategory;
  tickRulesGroup: ISymbolTickRulesGroup[];
  tradable: boolean;
  editable: boolean;
  closable: boolean;
  trackable: boolean;
  // Centroid
  notional: number; // To be implemented
}

export interface IGetOrderCountsResponse {
  marketOrderCounts: number;
  pendingOrderCounts: number;
}

export interface IDealHistory {
  orderId: string;
  symbol: string;
  volume: number;
  volumeFilled: number;
  lotSize: number;
  lotSizeFilled: number;
  units: number;
  decimal: number;
  openPrice: number;
  takeProfit: number;
  stopLoss: number;
  orderType: EOrderType;
  state: EOrderState;
  fillPolicy: EFillPolicy;
  status: EOrderStatus;
  deals: IDealDetail[];
  remarks: string;
  openTime: number;
  closeTime: number;
  isEnable: boolean;
}

export interface IDealDetail {
  dealId: string;
  symbol: string;
  takeProfit: number;
  stopLoss: number;
  lotSize: number;
  volume: number;
  units: number;
  swap: number;
  commission: number;
  fee: number;
  price: number;
  profit: number;
  dealType: EOrderDealType;
  direction: EOrderDealDirection;
  reason: EDealReason;
  comment: string;
  openTime: number;
}

export interface IOrderHistory {
  orderId: string;
  symbol: string;
  currency: string;
  volume: number;
  lotSize: number;
  decimal: number;
  contractSize: number;
  swap: number;
  units: number;
  openPrice: number;
  closePrice: number;
  takeProfit: number;
  stopLoss: number;
  profit: number;
  credit: number;
  deposit: number;
  withdrawal: number;
  realizedProfitLoss: number;
  commission: number;
  exchangeRate: number;
  profitTermCurrency: number;
  status: EOrderStatus;
  orderType: EOrderType;
  state: EOrderState;
  reason: EOrderReason;
  fillPolicy: EFillPolicy;
  marketFillPolicy: EFillPolicy[];
  orderBalanceType: EOrderBalanceType;
  comment: string;
  exitMethod: string;
  openTime: number;
  closeTime: number;
  expiration: number;
  isEnable: boolean;
  dealId: string;
  positionId: string;
  fee: number;
  dealType: EOrderDealType;
  dealDirection: EOrderDealDirection;
  totalLotSize: number;
  description: string;
  filledLotSize: number;
  remainedLotSize: number;
  stopPrice: number;
  limitPrice: number;
  avgFilledPrice: number;
  horizonOrderStatus: string;
  horizonTimeInForce: string;
  updatedTime: number;
  tickRulesGroup: ISymbolTickRulesGroup[];
  sequenceNo: number;
  orderReference: string;
  centroidOrderState: EOrderStateCentroid;
  notional: number;
  openCenOrder: string;
  closeCenOrder: string;
  closedTransactionType: string; // similar to dealType (Centroid only)
  tradable: boolean;
  editable: boolean;
  closable: boolean;
  trackable: boolean;
}

export interface IPendingOrder {
  orderId: string;
  symbol: string;
  volume: number;
  lotSize: number;
  decimal: number;
  units: number;
  lotSizeStep: number;
  remainedLotSize: number;
  filledLotSize: number;
  minContractLotSize: number;
  openPrice: number;
  closePrice: number;
  currentPrice: number;
  price: number;
  takeProfit: number;
  stopLoss: number;
  profit: number;
  marginRate: number;
  indicate: EOrderIndicate;
  exeMode: EExecutionMode;
  orderType: EOrderType;
  state: EOrderState;
  tradeExpiry: EOrderExpiry;
  fillPolicy: EFillPolicy;
  marketFillPolicy: EFillPolicy[];
  comment: string;
  openTime: number;
  closeTime: number;
  expiration: number;
  isEnable: boolean;
  priceTrigger: number;
  description: string;
  lastPrice: number;
  limitPrice: number;
  stopPrice: number;
  symbolType: ESymbolCategory;
  tickRulesGroup: ISymbolTickRulesGroup[];
  horizonOrderStatus: EOrderStatus;
  unfilledVolume: number;
  // Centroid
  id: number; // For Centroid Pending Order Deletion
  centroidDeviation?: number; // Not in swagger yet
  tradable: boolean;
  editable: boolean;
  closable: boolean;
  trackable: boolean;
}

export interface IUIOrderTableDisplay {
  avgBoughtPrice?: number;
  avgFilledPrice?: number;
  avgSoldPrice?: number;
  closable?: boolean;
  close?: string;
  closeDate?: number;
  comment?: string;
  currentPrice?: number;
  decimal?: number;
  delete?: string;
  edit?: string;
  editable?: boolean;
  expiryDate?: number;
  fee?: number;
  filledLotSize?: number;
  isEnable?: boolean;
  lastPrice?: number;
  limitPrice?: number;
  lotSize?: number;
  marketValue?: number;
  openDate?: number;
  openPrice?: number;
  openTime?: number;
  orderId?: string;
  orderType?: EOrderType;
  profit?: number;
  remainedLotSize?: number;
  status?: EOrderStatus;
  stopLoss?: number;
  stopPrice?: number;
  symbol?: string;
  symbolType?: ESymbolCategory;
  takeProfit?: number;
  tickRulesGroup?: ISymbolTickRulesGroup[];
  timeInForce?: number;
  track?: string;
  trackable?: boolean;
  tradeExpiry?: EOrderExpiry;
  units?: number;
  updatedTime?: number;
}

// Export interfaces
export interface IOrderExport {
  ascending?: boolean;
  timeZoneId?: string;
}

export interface IGetOrderExportParams extends IOrderExport {
  symbol?: string;
  sortingColumn?: string;
  hiddenColumns?: string[];
}

export interface IGetOrderExportPendingParams extends IOrderExport {
  symbol?: string;
  sortingColumn?: string;
  hiddenColumns?: string[];
}

export interface IGetOrderExportDealHistoriesParams extends IOrderExport {
  to: number;
  from: number;
  sortingColumn?: string;
  hiddenColumns?: string[];
}

export interface IGetOrderExportHistoriesParams extends IOrderExport {
  to: number;
  from: number;
  type: EOrderHistoryType;
  sortingColumn?: string;
  hiddenColumns?: string[];
  symbol?: string;
}
